import { useMemo, useState } from "react";
import { Card, Collapse, Space, Typography } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";

const ACCENT = "#1677ff";
const MUTED_BG = "#fafafa";
const BORDER = "#f0f0f0";

const { Paragraph, Text, Title } = Typography;

const labelStyle = { fontWeight: 600, fontSize: 15, lineHeight: 1.4 };

function PanelLabel({ children }) {
  return <span style={labelStyle}>{children}</span>;
}

const blockNote = {
  background: MUTED_BG,
  borderRadius: 8,
  padding: "12px 14px",
  marginBottom: 12,
  borderLeft: `3px solid ${ACCENT}`,
};
const blockNoteLast = { ...blockNote, marginBottom: 0 };

const listWrap = {
  margin: 0,
  padding: 0,
  listStyle: "none",
};

function ListItem({ title: itemTitle, children }) {
  return (
    <li style={{ paddingLeft: 14, borderLeft: `3px solid ${BORDER}`, marginBottom: 14 }}>
      <Text strong style={{ display: "block", marginBottom: 4 }}>
        {itemTitle}
      </Text>
      <Text type="secondary" style={{ fontSize: 14, lineHeight: 1.6 }}>
        {children}
      </Text>
    </li>
  );
}

const faqItems = [
  {
    key: "what",
    label: <PanelLabel>What does this project measure?</PanelLabel>,
    children: (
      <Paragraph
        type="secondary"
        style={{ fontSize: 14, lineHeight: 1.65, marginBottom: 0, maxWidth: 900 }}
      >
        We report measurable platform activity tied to each Custom Solution (CSOL): how many impressions were delivered,
        how many clicks and conversions occurred, and partner cost in USD over a defined period. Those figures come from
        The Trade Desk&apos;s standard GQL reporting, aligned to the audiences, campaigns, adgroups, bidlists, or other
        entities your solution uses.
      </Paragraph>
    ),
  },
  {
    key: "how",
    label: <PanelLabel>How is &quot;impact&quot; measured (in plain terms)?</PanelLabel>,
    children: (
      <Space direction="vertical" size={12} style={{ width: "100%", maxWidth: 900 }}>
        <div style={blockNote}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "rgba(0,0,0,0.85)" }}>
            Scope
          </Text>
          <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 14, lineHeight: 1.65 }}>
            Each custom solution logs the parts of the platform it touches (for example specific campaigns, audiences,
            or bidlists). That scope is fed in on a schedule with start and end dates that say when we should count that
            solution.
          </Paragraph>
        </div>
        <div style={blockNote}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "rgba(0,0,0,0.85)" }}>
            Reporting
          </Text>
          <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 14, lineHeight: 1.65 }}>
            For each item in scope, we pull reporting data from the platform for the time window we&apos;re reporting on.
          </Paragraph>
        </div>
        <div style={blockNoteLast}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "rgba(0,0,0,0.85)" }}>
            Reporting pack
          </Text>
          <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 14, lineHeight: 1.65 }}>
            Results are rolled up by solution / application, and can be enriched with context from Jira (owner, region,
            tier, etc.) so the leadership team can interpret numbers alongside program metadata.
          </Paragraph>
        </div>
      </Space>
    ),
  },
  {
    key: "assume",
    label: <PanelLabel>What we assume</PanelLabel>,
    children: (
      <ul style={{ ...listWrap, maxWidth: 900 }}>
        <ListItem title="The scope list is correct">
          If something is missing, misclassified, or has the wrong active dates, the numbers won&apos;t reflect the full
          story.
        </ListItem>
        <ListItem title="Platform reporting is authoritative">
          Impressions, clicks, conversions, and partner cost come from standard TTD reporting for the entities in scope.
        </ListItem>
        <ListItem title='"Active" solutions'>
          We only include a solution in a given report if its configured start/end window includes that report date
          (solutions marked ended before that date are excluded).
        </ListItem>
      </ul>
    ),
  },
  {
    key: "limitations",
    label: <PanelLabel>Limitations</PanelLabel>,
    children: (
      <ul style={{ ...listWrap, maxWidth: 900 }}>
        <ListItem title="Depends on entities being resolvable">
          Anything the system can&apos;t map cleanly to reportable entities may not appear until data or configuration is
          fixed.
        </ListItem>
        <ListItem title="Operational reality">
          Paused or inactive setups may not contribute, depending on how linking works in the platform when we measure.
        </ListItem>
        <ListItem title='Jira adds context, not math'>
          Fields on the ticket (including labels like &quot;impact&quot;) do not change the measured impressions or cost.
        </ListItem>
      </ul>
    ),
  },
];

const OUTER_PANEL_KEY = "faq";

const faqOuterLabel = (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 12, paddingRight: 8 }}>
    <QuestionCircleOutlined style={{ fontSize: 22, color: ACCENT, marginTop: 2 }} aria-hidden />
    <div>
      <Title level={4} style={{ margin: "0 0 4px 0", fontWeight: 600 }}>
        Frequently asked questions
      </Title>
      <Text type="secondary" style={{ fontSize: 14 }}>
        How impact is defined, what the dashboard assumes, and where numbers can fall short.
      </Text>
    </div>
  </div>
);

function FaqSection() {
  /**
   * Must be real arrays (`[]`), not `undefined`. rc-collapse treats `undefined` activeKey as uncontrolled
   * and can open the first panel by default.
   */
  const [outerOpenKeys, setOuterOpenKeys] = useState([]);
  const [innerOpenKeys, setInnerOpenKeys] = useState([]);

  const outerItems = useMemo(
    () => [
      {
        key: OUTER_PANEL_KEY,
        label: faqOuterLabel,
        children: (
          <Collapse
            activeKey={innerOpenKeys}
            bordered={false}
            expandIconPosition="end"
            onChange={setInnerOpenKeys}
            size="large"
            items={faqItems}
            style={{ background: "transparent" }}
            className="faq-collapse faq-collapse-inner"
          />
        ),
      },
    ],
    [innerOpenKeys],
  );

  return (
    <Card
      bordered={false}
      styles={{
        body: { paddingTop: 12 },
      }}
      style={{
        marginTop: 28,
        borderTop: `3px solid ${ACCENT}`,
        borderRadius: 8,
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
      }}
    >
      <Collapse
        activeKey={outerOpenKeys}
        bordered={false}
        className="faq-collapse faq-collapse-outer"
        expandIconPosition="end"
        items={outerItems}
        onChange={setOuterOpenKeys}
        size="large"
        style={{ background: "transparent" }}
      />
    </Card>
  );
}

export default FaqSection;
